import {
    Calendar,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Circle,
    Clock,
    Compass,
    MessageCircle,
    Search,
    Sparkles,
    Sun,
    UserCircle2,
    X,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
    DOC_KIND_LABEL,
    MOCK_DOCUMENTS,
    MOCK_FAMILY,
    MOCK_PROVIDERS,
    MOCK_ROADMAP,
    MockDocument,
    MockRoadmapMilestone,
    MockRoadmapTask,
    ONBOARDING_DOC_ID,
    PROVIDER_TYPE_LABEL,
    ROADMAP_PHASES,
    RoadmapPhase,
} from './mockData'

type ItemStatus = 'completed' | 'in-progress' | 'not-started'

type ComputedItem = {
    milestone: MockRoadmapMilestone
    tasks: MockRoadmapTask[]
    completed: number
    total: number
    status: ItemStatus
}

export default function Roadmap() {
    const [taskState, setTaskState] = useState<Record<string, boolean>>(() => {
        const seed: Record<string, boolean> = {}
        MOCK_ROADMAP.forEach((m) => m.tasks.forEach((t) => (seed[t.id] = t.isChecked)))
        return seed
    })
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})
    const [openSourceDocId, setOpenSourceDocId] = useState<string | null>(null)

    const visible = useMemo(
        () => MOCK_ROADMAP.filter((m) => m.changeKind !== 'archived'),
        []
    )

    const computed: ComputedItem[] = useMemo(
        () =>
            visible.map((m) => {
                const tasks = m.tasks.map((t) => ({ ...t, isChecked: !!taskState[t.id] }))
                const completed = tasks.filter((t) => t.isChecked).length
                const total = tasks.length
                let status: ItemStatus = 'not-started'
                if (total > 0 && completed === total) status = 'completed'
                else if (m.status === 'completed') status = 'completed'
                else if (m.status === 'in-progress' || completed > 0) status = 'in-progress'
                return { milestone: m, tasks, completed, total, status }
            }),
        [visible, taskState]
    )

    const completedItems = computed.filter((s) => s.status === 'completed')
    const activeItems = computed.filter((s) => s.status !== 'completed')

    function toggleTask(taskId: string) {
        setTaskState((prev) => ({ ...prev, [taskId]: !prev[taskId] }))
    }
    function toggleExpand(id: string) {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    const openDoc = openSourceDocId ? MOCK_DOCUMENTS.find((d) => d.id === openSourceDocId) : null

    return (
        <div className='flex-1 mx-auto w-full max-w-7xl'>
            <HeroHeader
                providerCount={MOCK_PROVIDERS.length}
                documentCount={MOCK_DOCUMENTS.length}
            />
            <main className='px-3 py-8 sm:mx-10 sm:px-6'>
                <div className='mx-auto max-w-5xl'>
                    {completedItems.length > 0 && (
                        <CompletedSection
                            items={completedItems}
                            expanded={expanded}
                            onToggleExpand={toggleExpand}
                            onToggleTask={toggleTask}
                            onOpenSource={setOpenSourceDocId}
                        />
                    )}

                    {ROADMAP_PHASES.map((phase) => {
                        const itemsInPhase = activeItems.filter(
                            (i) => i.milestone.phase === phase.id
                        )
                        return (
                            <PhaseSection
                                key={phase.id}
                                phaseId={phase.id}
                                label={phase.label}
                                horizon={phase.horizon}
                                blurb={phase.blurb}
                                items={itemsInPhase}
                                expanded={expanded}
                                onToggleExpand={toggleExpand}
                                onToggleTask={toggleTask}
                                onOpenSource={setOpenSourceDocId}
                            />
                        )
                    })}

                    <p className='mt-8 text-xs text-gray-400 italic text-center'>
                        Your care plan is a living document. Items shift between phases as
                        priorities change.
                    </p>
                </div>
            </main>

            {openDoc && (
                <SourceModal doc={openDoc} onClose={() => setOpenSourceDocId(null)} />
            )}
        </div>
    )
}

function HeroHeader({
    providerCount,
    documentCount,
}: {
    providerCount: number
    documentCount: number
}) {
    return (
        <header className='relative overflow-hidden bg-gradient-to-r from-brand-800 via-brand-600 to-brand-500'>
            <div className='absolute inset-0 overflow-hidden'>
                <div className='absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary-300/20 blur-3xl' />
                <div className='absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-brand-300/30 blur-3xl' />
                <div
                    className={
                        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ' +
                        'w-[600px] h-[600px] rounded-full bg-white/5 blur-3xl'
                    }
                />
            </div>
            <div className='relative px-4 sm:px-6 lg:px-8 py-6 sm:py-10'>
                <div className='mx-auto max-w-5xl'>
                    <div className='flex items-start justify-between'>
                        <div>
                            <div className='flex items-center gap-2 mb-3'>
                                <span
                                    className={
                                        'inline-flex items-center gap-1.5 px-3 py-1 rounded-full ' +
                                        'bg-brand-100/10 backdrop-blur-sm text-white/90 text-sm font-medium'
                                    }
                                >
                                    <Sun className='w-5 h-5 text-secondary-300' />
                                    Multi-source roadmap for {MOCK_FAMILY.childFirstName}
                                </span>
                            </div>
                            <h1 className='text-2xl sm:text-2xl lg:text-3xl font-bold text-white mb-2'>
                                My Journey
                            </h1>
                            <p className='text-brand-100 text-sm sm:text-lg max-w-xl'>
                                Now informed by {providerCount} providers and {documentCount} documents,
                                in addition to your original onboarding answers.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className='absolute bottom-0 left-0 right-0'>
                <div className='absolute bottom-0 left-0 right-0 h-1 bg-white' />
                <svg
                    viewBox='0 0 1440 48'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    className='w-full h-auto relative z-10 translate-y-[1px]'
                    preserveAspectRatio='none'
                >
                    <path
                        d={
                            'M0 48V24C180 36 360 12 540 24C720 36 900 12 1080 24C1260 36 ' +
                            '1440 24 1440 24V48H0Z'
                        }
                        className='fill-white/50'
                    />
                    <path
                        d={
                            'M0 48V32C180 42 360 22 540 32C720 42 900 22 1080 32C1260 42 ' +
                            '1440 32 1440 32V48H0Z'
                        }
                        className='fill-white'
                    />
                    <rect x='0' y='47' width='1440' height='5' className='fill-white' />
                </svg>
            </div>
        </header>
    )
}

function CompletedSection({
    items,
    expanded,
    onToggleExpand,
    onToggleTask,
    onOpenSource,
}: {
    items: ComputedItem[]
    expanded: Record<string, boolean>
    onToggleExpand: (id: string) => void
    onToggleTask: (taskId: string) => void
    onOpenSource: (docId: string) => void
}) {
    const [isOpen, setIsOpen] = useState(false)
    return (
        <div className='mb-6 bg-white rounded-xl border border-gray-200 shadow-sm'>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className='flex justify-between items-center p-4 w-full text-left'
            >
                <div className='flex gap-3 items-center'>
                    <CheckCircle className='w-5 h-5 text-green-500' />
                    <div>
                        <h3 className='text-base font-semibold text-gray-900'>
                            Completed ({items.length})
                        </h3>
                        <p className='text-sm text-gray-500'>Items you&apos;ve finished</p>
                    </div>
                </div>
                {isOpen ? (
                    <ChevronUp className='w-5 h-5 text-gray-400' />
                ) : (
                    <ChevronDown className='w-5 h-5 text-gray-400' />
                )}
            </button>
            {isOpen && (
                <div className='px-4 pb-4 space-y-3'>
                    {items.map((item) => (
                        <ItemCard
                            key={item.milestone.id}
                            item={item}
                            isExpanded={!!expanded[item.milestone.id]}
                            onToggleExpand={() => onToggleExpand(item.milestone.id)}
                            onToggleTask={onToggleTask}
                            onOpenSource={onOpenSource}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function PhaseSection({
    phaseId,
    label,
    horizon,
    blurb,
    items,
    expanded,
    onToggleExpand,
    onToggleTask,
    onOpenSource,
}: {
    phaseId: RoadmapPhase
    label: string
    horizon: string
    blurb: string
    items: ComputedItem[]
    expanded: Record<string, boolean>
    onToggleExpand: (id: string) => void
    onToggleTask: (taskId: string) => void
    onOpenSource: (docId: string) => void
}) {
    const accent = PHASE_ACCENT[phaseId]
    return (
        <section className='mb-8'>
            <div className='flex items-center gap-3 mb-3'>
                <div
                    className={
                        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ' +
                        accent.iconBg
                    }
                >
                    <Compass className={'w-5 h-5 ' + accent.iconText} />
                </div>
                <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-2'>
                        <h2 className='text-lg font-semibold text-gray-900'>{label}</h2>
                        <span className={'text-xs font-medium uppercase tracking-wider ' + accent.horizonText}>
                            {horizon}
                        </span>
                    </div>
                    <p className='text-xs text-gray-500'>{blurb}</p>
                </div>
            </div>

            {items.length === 0 ? (
                <div className='bg-white border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500'>
                    Nothing here yet. As your team uploads more, items will surface here.
                </div>
            ) : (
                <div className='space-y-3'>
                    {items.map((item) => (
                        <ItemCard
                            key={item.milestone.id}
                            item={item}
                            isExpanded={!!expanded[item.milestone.id]}
                            onToggleExpand={() => onToggleExpand(item.milestone.id)}
                            onToggleTask={onToggleTask}
                            onOpenSource={onOpenSource}
                        />
                    ))}
                </div>
            )}
        </section>
    )
}

const PHASE_ACCENT: Record<RoadmapPhase, { iconBg: string; iconText: string; horizonText: string }> = {
    'current-focus': {
        iconBg: 'bg-brand-100',
        iconText: 'text-brand-700',
        horizonText: 'text-brand-700',
    },
    'upcoming-focus': {
        iconBg: 'bg-amber-100',
        iconText: 'text-amber-700',
        horizonText: 'text-amber-700',
    },
    'looking-ahead': {
        iconBg: 'bg-gray-100',
        iconText: 'text-gray-600',
        horizonText: 'text-gray-500',
    },
}

function ItemCard({
    item,
    isExpanded,
    onToggleExpand,
    onToggleTask,
    onOpenSource,
}: {
    item: ComputedItem
    isExpanded: boolean
    onToggleExpand: () => void
    onToggleTask: (taskId: string) => void
    onOpenSource: (docId: string) => void
}) {
    const { milestone, tasks, completed, total, status } = item
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0

    return (
        <div
            onClick={() => {
                if (!isExpanded) onToggleExpand()
            }}
            className={
                'bg-white rounded-xl shadow-sm border transition-all cursor-pointer ' +
                (status === 'in-progress' ? 'border-brand-200' : 'border-gray-200')
            }
        >
            <div className='p-5'>
                <div className='flex items-start gap-3 mb-2'>
                    <StatusIcon status={status} />
                    <div className='flex-1 min-w-0'>
                        <div className='flex flex-wrap items-center gap-2 mb-0.5'>
                            <h3 className='text-base font-semibold text-gray-900'>{milestone.title}</h3>
                            <StatusPill status={status} />
                        </div>
                        <p className='text-sm text-gray-600'>{milestone.description}</p>
                        <div className='flex gap-2 items-center mt-2 text-xs text-gray-500'>
                            <Calendar className='w-3.5 h-3.5' />
                            <span>{milestone.timeframe}</span>
                            {total > 0 && (
                                <>
                                    <span className='text-gray-300'>·</span>
                                    <span>
                                        {completed} / {total} steps
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {!isExpanded ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                onToggleExpand()
                            }}
                            className='flex items-center gap-1 text-xs text-brand-700/70 flex-shrink-0'
                        >
                            Show more
                            <ChevronDown className='w-4 h-4' />
                        </button>
                    ) : (
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                onToggleExpand()
                            }}
                            className='flex items-center gap-1 text-xs text-brand-700/70 flex-shrink-0'
                        >
                            Show less
                            <ChevronUp className='w-4 h-4' />
                        </button>
                    )}
                </div>

                {isExpanded && (
                    <div className='mt-4 pt-4 border-t border-gray-100'>
                        {total > 0 && (
                            <div className='mb-4'>
                                <div className='flex justify-between items-center mb-2'>
                                    <span className='text-sm font-medium text-gray-700'>Progress</span>
                                    <span className='text-sm text-gray-500'>
                                        {completed} / {total}
                                    </span>
                                </div>
                                <div className='w-full h-2 rounded-full bg-secondary-200'>
                                    <div
                                        className='h-2 rounded-full transition-all duration-300 ease-out bg-brand-500'
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {tasks.length > 0 && (
                            <div className='mb-4'>
                                <h4 className='mb-2 text-sm font-medium text-gray-700'>Recommended Steps</h4>
                                <div className='space-y-1'>
                                    {tasks.map((task) => (
                                        <TaskRow
                                            key={task.id}
                                            task={task}
                                            onToggle={() => onToggleTask(task.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <SourcesBlock
                            sourceIds={milestone.sourceDocIds}
                            onOpenSource={onOpenSource}
                        />

                        <div className='space-y-2 mt-4'>
                            <button
                                onClick={(e) => e.stopPropagation()}
                                className={
                                    'flex gap-3 items-center p-2.5 w-full text-left rounded-full ' +
                                    'border transition-colors text-brand-600 bg-brand-50/80 hover:bg-brand-100'
                                }
                            >
                                <MessageCircle className='w-4 h-4' />
                                <span className='text-sm font-medium'>
                                    Ask BrightPath for help with these steps
                                </span>
                            </button>
                            <button
                                onClick={(e) => e.stopPropagation()}
                                className={
                                    'flex gap-3 items-center p-2.5 w-full text-left rounded-full ' +
                                    'border transition-colors text-brand-600 bg-brand-50 hover:bg-brand-100'
                                }
                            >
                                <Search className='w-4 h-4' />
                                <span className='text-sm font-medium'>Go to Resource Library</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

function StatusIcon({ status }: { status: ItemStatus }) {
    if (status === 'completed') {
        return (
            <div className='w-7 h-7 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0'>
                <CheckCircle className='w-5 h-5 text-green-500' />
            </div>
        )
    }
    if (status === 'in-progress') {
        return (
            <div className='w-7 h-7 rounded-full bg-brand-400 flex items-center justify-center flex-shrink-0'>
                <Clock className='w-4 h-4 text-white' />
            </div>
        )
    }
    return (
        <div className='w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0'>
            <Circle className='w-5 h-5 text-gray-300' />
        </div>
    )
}

function StatusPill({ status }: { status: ItemStatus }) {
    if (status === 'completed') {
        return (
            <span className='text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700'>
                Completed
            </span>
        )
    }
    if (status === 'in-progress') {
        return (
            <span className='text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-brand-100 text-brand-900'>
                In progress
            </span>
        )
    }
    return (
        <span className='text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600'>
            Not started
        </span>
    )
}

function TaskRow({ task, onToggle }: { task: MockRoadmapTask; onToggle: () => void }) {
    return (
        <div
            onClick={(e) => e.stopPropagation()}
            className='flex items-start px-3 py-1 space-x-3 transition-colors rounded-lg hover:bg-gray-50 group'
        >
            <input
                type='checkbox'
                checked={task.isChecked}
                onChange={onToggle}
                className='w-5 h-5 mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer'
            />
            <div className='flex-1'>
                <div
                    className={
                        'text-sm font-medium transition-all duration-200 ' +
                        (task.isChecked
                            ? 'text-gray-400 line-through'
                            : 'text-gray-700 group-hover:text-gray-900')
                    }
                >
                    {task.label}
                </div>
            </div>
        </div>
    )
}

function SourcesBlock({
    sourceIds,
    onOpenSource,
}: {
    sourceIds: string[]
    onOpenSource: (docId: string) => void
}) {
    const sources = MOCK_DOCUMENTS.filter((d) => sourceIds.includes(d.id))
    if (sources.length === 0) return null
    return (
        <div className='mb-3 pt-3 border-t border-gray-100'>
            <h4 className='mb-2 text-sm font-medium text-gray-700'>Sources</h4>
            <div className='flex flex-wrap items-center gap-1.5'>
                {sources.map((s) => {
                    const prov = MOCK_PROVIDERS.find((p) => p.id === s.providerId)
                    return (
                        <button
                            key={s.id}
                            type='button'
                            onClick={(e) => {
                                e.stopPropagation()
                                onOpenSource(s.id)
                            }}
                            className={
                                'inline-flex items-center gap-1 px-2 py-1 ' +
                                'bg-white border border-gray-200 rounded-full text-xs ' +
                                'text-gray-700 hover:border-brand-400 hover:text-brand-700 ' +
                                'transition-colors'
                            }
                        >
                            {s.isOnboarding ? (
                                <UserCircle2 className='w-3 h-3 text-brand-600' />
                            ) : (
                                <Sparkles className='w-3 h-3 text-brand-600' />
                            )}
                            {s.title}
                            {prov && (
                                <span className='text-gray-400'>
                                    · {prov.clinicianName.split(',')[0]}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

function SourceModal({ doc, onClose }: { doc: MockDocument; onClose: () => void }) {
    const prov = MOCK_PROVIDERS.find((p) => p.id === doc.providerId)
    const isOnboarding = doc.id === ONBOARDING_DOC_ID
    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/50'
            onClick={onClose}
        >
            <div
                className='bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto'
                onClick={(e) => e.stopPropagation()}
            >
                <div className='flex items-start justify-between gap-3 p-5 border-b border-gray-100'>
                    <div className='flex items-start gap-3 flex-1 min-w-0'>
                        <div className='w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0'>
                            {isOnboarding ? (
                                <UserCircle2 className='w-5 h-5 text-brand-600' />
                            ) : (
                                <Sparkles className='w-5 h-5 text-brand-600' />
                            )}
                        </div>
                        <div className='flex-1 min-w-0'>
                            <p className='text-[10px] uppercase tracking-wider text-gray-500 mb-0.5'>
                                {isOnboarding ? 'Family input (onboarding)' : DOC_KIND_LABEL[doc.kind]}
                            </p>
                            <h2 className='text-base font-semibold text-gray-900'>{doc.title}</h2>
                            <p className='text-xs text-gray-500 mt-1'>
                                {isOnboarding ? (
                                    <>Completed {doc.uploadedAt}</>
                                ) : (
                                    <>
                                        {doc.fileName} · {doc.pageCount} pages · uploaded {doc.uploadedAt}
                                    </>
                                )}
                            </p>
                            {prov && !isOnboarding && (
                                <p className='text-xs text-gray-700 mt-1'>
                                    From{' '}
                                    <a
                                        href={`/admin/family-care-team-preview/team/${prov.id}`}
                                        className='font-medium text-brand-700 hover:underline'
                                    >
                                        {prov.clinicianName}
                                    </a>{' '}
                                    <span className='text-gray-500'>
                                        ({PROVIDER_TYPE_LABEL[prov.type]})
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className='p-1 text-gray-400 hover:text-gray-700 flex-shrink-0'
                        aria-label='Close'
                    >
                        <X className='w-5 h-5' />
                    </button>
                </div>

                <div className='p-5 border-b border-gray-100'>
                    <div className='flex items-center gap-1.5 mb-2'>
                        <Sparkles className='w-3.5 h-3.5 text-brand-700' />
                        <span className='text-[10px] uppercase tracking-wider text-brand-700 font-semibold'>
                            What BrightPath learned
                        </span>
                    </div>
                    <p className='text-sm text-gray-800'>{doc.summary}</p>
                </div>

                <div className='p-5'>
                    <p className='text-[10px] uppercase tracking-wider text-gray-500 mb-3'>
                        Extracted findings ({doc.findings.length})
                    </p>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                        {doc.findings.map((f) => (
                            <div
                                key={f.label}
                                className='bg-gray-50 border border-gray-200 rounded-lg p-3'
                            >
                                <div className='flex items-center justify-between gap-2 mb-1'>
                                    <span className='text-[10px] uppercase tracking-wider text-gray-500'>
                                        {f.label}
                                    </span>
                                    {f.impactsRoadmap && (
                                        <span
                                            className={
                                                'inline-flex items-center gap-0.5 text-[10px] text-brand-700 ' +
                                                'bg-brand-50 px-1.5 py-0.5 rounded'
                                            }
                                        >
                                            <Sparkles className='w-2.5 h-2.5' />
                                            Roadmap
                                        </span>
                                    )}
                                </div>
                                <p className='text-sm text-gray-900'>{f.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
